/**
 * AnalyticsAreaDrawerField — the drawn map area's presence in the Filters drawer
 * (VERA-260).
 *
 * ## The gap this closes
 *
 * `filters.area` (the ring drawn on the scans map, VRG-480) already had an applied-filter
 * chip — `Location: drawn` — and no representation in the drawer whatsoever. That made the
 * drawer a LIE BY OMISSION about the page's state: a reader who opened it to answer "what
 * is narrowing this page" got six fields, none of which mentioned that a geographic shape
 * was excluding most of the data. The chip row is one scroll position away from the drawer
 * and neither surface pointed at the other.
 *
 * ## It reports and clears; it deliberately does NOT edit
 *
 * The value is a polygon. There is no control in a 500px side panel that can express one,
 * and the surface that CAN — the map's own draw tool — already exists and is where the
 * ring came from. So this field does the two things a panel can honestly do with a shape:
 * say that one is applied, and offer the two exits.
 *
 *  - **"View on map"** scrolls the map card back into view (`focusScansMap`). This is the
 *    real answer to "how do I change it": the editing surface is the map, so the field
 *    sends the reader there rather than pretending to own the value.
 *  - **The field's own `Clear`** (from the shared `FilterField` header, so it looks and
 *    behaves exactly like every other field's) removes the ring.
 *
 * ⚠️ `focusScansMap` returns `false` when no map card is on the canvas — a reader can
 * remove the card from Layout while an area filter is still applied. In that state there
 * is nowhere to send them, so the action is hidden rather than rendered as a control that
 * silently does nothing (§46: an unavailable action whose blocker is RECORD STATE is
 * hidden, not disabled — and hiding creates the obligation that the RESOLVING action stays
 * reachable, which `Clear` is).
 *
 * ## Why it renders nothing when no area is drawn
 *
 * Unlike the six dimension fields, this one has no editable empty state to offer — with no
 * ring there is no shape to describe and no map gesture this panel can start. An
 * always-present "Area: none, draw one on the map" row would be a permanent instruction in
 * a panel whose other rows are all controls. So it appears exactly when it has something
 * to report, which is also when the `Location: drawn` chip appears.
 *
 * ## Why it is an `extraFilter` and not a `columns` entry
 *
 * Same mechanical reason the date range is (see `AnalyticsFiltersHeader`'s `columns`):
 * a `columns` entry becomes an `internalFilters` member in `Filters.tsx`, and `area` is not
 * an array of ids — the drawer's staged `{field, operator, value}` machinery has nothing to
 * do with a ring, and routing it through would put a `GeoPoint[]` where every other member
 * is a `string[]`. It writes straight to redux, exactly as the map's own draw control and
 * the applied-filter chip's `X` already do.
 */
import React, { useEffect, useState } from 'react'

import { Stack, Typography } from '@mui/material'

import { MapPinnedIcon } from 'lucide-react'

import { Button } from '@repo/ui'

import { FilterField } from 'components/input/filters/FilterField'
// The same import `AnalyticsAppliedFilters` already makes for the `Location: drawn`
// chip's click target — one anchor, one scroll helper, two surfaces that need it (§36).
import {
  SCANS_MAP_ANCHOR_ID,
  focusScansMap,
} from 'pages/main/AnalyticsPage/components/ScansByLocation/ScansByLocation'

interface AnalyticsAreaDrawerFieldProps {
  /** `state.analytics.filters.area` — `null` when nothing is drawn. See
   *  `AnalyticsFilters.area` for why `null` and not `[]`. */
  area: unknown[] | null
  /** Clear the ring. Wired to `setAnalyticsFilters({ area: null })` by the caller. */
  onClear: () => void
}

export const AnalyticsAreaDrawerField = ({ area, onClear }: AnalyticsAreaDrawerFieldProps) => {
  /**
   * Is the map card actually on the canvas right now?
   *
   * In an EFFECT, not read during render. The answer is a DOM query, and the drawer
   * unmounts its fields on close — so this runs afresh every time the panel opens, which
   * is exactly when the answer can have changed (the reader may have removed the map card
   * from Layout since last time). Reading it during render would be a render-phase DOM
   * read for no benefit: the effect lands in the same commit the drawer's own slide does.
   *
   * Defaults to `false`, so the frame before the effect shows no button rather than a
   * button that would no-op if clicked.
   */
  const [mapOnCanvas, setMapOnCanvas] = useState(false)
  useEffect(() => {
    setMapOnCanvas(Boolean(document.getElementById(SCANS_MAP_ANCHOR_ID)))
  }, [area])

  // `null` is the whole test — a ring is only ever stored at 3+ vertices, so there is no
  // degenerate non-null value to guard against (`AnalyticsFilters.area`).
  if (area === null) return null

  return (
    /**
     * "Location", NOT "Area" — the same noun the applied-filter chip uses, and for the
     * reason `AnalyticsAppliedFilters`'s `FIELD_LABELS` states: both this and the country
     * filter narrow the page by WHERE, and a reader holds one concept, not two. The VALUE
     * distinguishes them — a country name there, "Area drawn on the map" here.
     *
     * `count={1}` so the field's header shows the same count badge every other applied
     * field shows. One ring is one constraint.
     */
    <FilterField label="Location" count={1} showClear onClear={onClear}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
          {/* House lucide weight (§21). A pinned-map glyph rather than a lasso: the lasso
              is the GESTURE that produced this and lives on the map's own control, whereas
              this row is about the RESULT. */}
          <MapPinnedIcon size={18} strokeWidth={1} />
          <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
            Area drawn on the map
          </Typography>
        </Stack>
        {/* Default 40px size — the shared Button is never `small` (§60). Hidden entirely
            when the map card is not on the canvas; see the docblock. */}
        {mapOnCanvas && (
          <Button variant="secondary" onClick={() => focusScansMap()}>
            View on map
          </Button>
        )}
      </Stack>
    </FilterField>
  )
}

export default AnalyticsAreaDrawerField
